import { useState } from 'react';
import { Plus } from 'lucide-react';
import { AdminLoadingState } from '../../components/primitives/admin-loading-state.js';
import { AdminEmptyState } from '../../components/primitives/admin-empty-state.js';
import { AdminErrorState } from '../../components/primitives/admin-error-state.js';
import { AdminStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { formatPaise } from '../../lib/format.js';
import { useAdminVariants, useAdminVariantMutations } from '../hooks/use-variants.js';
import { VariantForm } from './variant-form.js';
import type { CreateVariantInput } from '../api/catalog-api.js';

export interface ProductVariantsTabProps {
  productId: string;
}

export function ProductVariantsTab({ productId }: ProductVariantsTabProps) {
  const { data: variants, isLoading, isError, refetch } = useAdminVariants(productId);
  const { create, update, activate, deactivate } = useAdminVariantMutations(productId);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (isLoading) return <AdminLoadingState label="Loading variants" />;
  if (isError) return <AdminErrorState message="Variants could not be loaded." onRetry={() => void refetch()} />;

  const rows = variants ?? [];

  function handleCreate(input: CreateVariantInput) {
    create.mutate(input, { onSuccess: () => setShowCreate(false) });
  }

  function handleUpdate(variantId: string, input: CreateVariantInput) {
    update.mutate({ variantId, input }, { onSuccess: () => setEditingId(null) });
  }

  return (
    <div className="space-y-4">
      {rows.length === 0 && !showCreate && (
        <AdminEmptyState
          title="No variants yet"
          description="A product needs at least one sellable variant before customers can buy it."
        />
      )}

      {rows.map((variant) =>
        editingId === variant.id ? (
          <VariantForm
            key={variant.id}
            initial={{
              sku: variant.sku,
              variantName: variant.variantName,
              unitOfMeasure: variant.unitOfMeasure,
              netQuantity: variant.netQuantity,
              mrpPaise: variant.mrp.amountPaise,
              sellingPricePaise: variant.sellingPrice.amountPaise,
              barcode: variant.barcode,
              weightGrams: variant.weightGrams,
              lengthMm: variant.lengthMm,
              widthMm: variant.widthMm,
              heightMm: variant.heightMm,
            }}
            skuLocked
            submitLabel="Save changes"
            isSubmitting={update.isPending}
            onSubmit={(input) => handleUpdate(variant.id, input)}
            onCancel={() => setEditingId(null)}
          />
        ) : (
          <div
            key={variant.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 p-3"
          >
            <div>
              <p className="text-sm font-medium text-gray-900">{variant.variantName}</p>
              <p className="text-xs text-gray-400">
                SKU {variant.sku} · {variant.netQuantity} {variant.unitOfMeasure}
                {variant.barcode && ` · Barcode ${variant.barcode}`}
              </p>
              {(variant.weightGrams || variant.lengthMm || variant.widthMm || variant.heightMm) && (
                <p className="text-xs text-gray-400">
                  {variant.weightGrams && `${variant.weightGrams}g`}
                  {(variant.lengthMm || variant.widthMm || variant.heightMm) &&
                    `${variant.weightGrams ? ' · ' : ''}${variant.lengthMm ?? '—'}×${variant.widthMm ?? '—'}×${variant.heightMm ?? '—'}mm`}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-gray-900">{formatPaise(variant.sellingPrice.amountPaise)}</span>
              {variant.mrp.amountPaise !== variant.sellingPrice.amountPaise && (
                <span className="text-gray-400 line-through">{formatPaise(variant.mrp.amountPaise)}</span>
              )}
              <AdminStatusBadge label={variant.isActive ? 'Active' : 'Inactive'} tone={variant.isActive ? 'success' : 'neutral'} />
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setEditingId(variant.id)}
                className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
              >
                Edit
              </button>
              {variant.isActive ? (
                <button
                  type="button"
                  onClick={() => deactivate.mutate(variant.id)}
                  className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                >
                  Deactivate
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => activate.mutate(variant.id)}
                  className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                >
                  Activate
                </button>
              )}
            </div>
          </div>
        ),
      )}

      {showCreate ? (
        <VariantForm
          isSubmitting={create.isPending}
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="focus-visible:ring-brand inline-flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add variant
        </button>
      )}
    </div>
  );
}
